import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Tooltip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Close as CloseIcon,
  Timeline as TimelineIcon,
  WarningAmber as WarningAmberIcon,
} from '@mui/icons-material';
import type {
  TaxReturnTrackerResult,
  TaxReturnTicketTracker,
} from '../services/taxReturnTrackerService';
import {
  STAGE_COLUMN_MAP,
  formatStageLabel,
  formatTaxYear,
} from '../services/taxReturnTrackerService';
import type {
  TaxReturnNote,
  TaxReturnNoteStatus,
} from '../services/taxReturnNotesService';
import {
  filterNotesByCurrentStage,
  groupNotesByStatus,
} from '../services/taxReturnNotesService';

export interface TaxReturnStatusTrackerProps {
  loading: boolean;
  error: string;
  result: TaxReturnTrackerResult | null;
  notes: TaxReturnNote[];
}

const tileSx = {
  bgcolor: 'background.paper',
  borderRadius: '20px',
  p: 2.25,
  border: '1px solid',
  borderColor: 'rgba(25, 118, 210, 0.12)',
  boxShadow: '0 4px 18px rgba(25, 118, 210, 0.06)',
  overflow: 'hidden',
  position: 'relative' as const,
  minHeight: 0,
  textAlign: 'left' as const,
  transition:
    'box-shadow 0.35s ease, border-color 0.35s ease, transform 0.35s ease',
  '&:hover': {
    boxShadow: '0 10px 28px rgba(25, 118, 210, 0.12)',
    borderColor: 'rgba(25, 118, 210, 0.28)',
  },
};

const IconWell: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      width: 36,
      height: 36,
      borderRadius: 2,
      bgcolor: 'rgba(25, 118, 210, 0.08)',
      color: 'primary.main',
      display: 'grid',
      placeItems: 'center',
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

type TrackerNotesUi = 'rail' | 'legacy';

/**
 * Tracker notes UI variant.
 * - 'rail'   → compact red teasers above status; full detail in modal
 * - 'legacy' → previous amber cards above each status
 */
const TRACKER_NOTES_UI = 'rail' as TrackerNotesUi;

const NOTES_ACCENT = {
  main: '#c62828',
  dark: '#8e0000',
  softBg: 'rgba(198, 40, 40, 0.06)',
  softBgStrong: 'rgba(198, 40, 40, 0.12)',
  border: 'rgba(198, 40, 40, 0.22)',
  borderHover: 'rgba(198, 40, 40, 0.4)',
  shadow: 'rgba(198, 40, 40, 0.1)',
  shadowHover: 'rgba(198, 40, 40, 0.16)',
  ring: 'rgba(198, 40, 40, 0.14)',
};

const NOTE_TEASER_CHARS = 42;
const NOTES_VISIBLE_PER_STAGE = 3;

function formatNoteWhen(note: TaxReturnNote): string {
  const when = note.updated || note.created;
  if (!when) return '';
  return new Date(when).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function staffDisplayName(staff: TaxReturnNote['staff']): string {
  if (!staff) return 'Team note';
  return (
    staff.name ||
    [staff.preferredName || staff.firstname, staff.lastname].filter(Boolean).join(' ') ||
    'Team note'
  );
}

/** Legacy amber warning card — kept for TRACKER_NOTES_UI = 'legacy' */
const StatusWarningNote: React.FC<{
  note: TaxReturnNote;
  compact?: boolean;
}> = ({ note, compact }) => {
  const whenLabel = formatNoteWhen(note);

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 0.75,
        alignItems: 'flex-start',
        px: compact ? 0.75 : 1,
        py: compact ? 0.6 : 0.85,
        mb: 0.6,
        borderRadius: 1.5,
        border: '1px solid',
        borderColor: 'rgba(237, 108, 2, 0.35)',
        borderLeftWidth: 3,
        borderLeftColor: 'warning.main',
        background:
          'linear-gradient(135deg, rgba(255, 243, 224, 0.95) 0%, rgba(255, 248, 225, 0.7) 100%)',
        boxShadow: '0 4px 12px rgba(237, 108, 2, 0.08)',
      }}
    >
      <WarningAmberIcon
        sx={{
          fontSize: compact ? 14 : 16,
          color: 'warning.dark',
          mt: '1px',
          flexShrink: 0,
        }}
      />
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            color: 'warning.dark',
            fontWeight: 600,
            lineHeight: 1.35,
            fontSize: compact ? '0.65rem' : '0.72rem',
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
          }}
        >
          {note.notes}
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.35,
            color: 'text.secondary',
            fontSize: '0.6rem',
            lineHeight: 1.35,
          }}
        >
          {note.staff ? (
            <>
              <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
                {note.staff.name}
              </Box>
              {whenLabel ? ` · ${whenLabel}` : ''}
            </>
          ) : (
            <>Team note{whenLabel ? ` · ${whenLabel}` : ''}</>
          )}
        </Typography>
      </Box>
    </Box>
  );
};

/** One-line teaser above a status — click opens detail modal */
const StatusNoteTeaser: React.FC<{
  note: TaxReturnNote;
  stageName: string;
  onOpen: () => void;
  showStage?: boolean;
}> = ({ note, stageName, onOpen, showStage = true }) => {
  const teaser =
    note.notes.length > NOTE_TEASER_CHARS
      ? `${note.notes.slice(0, NOTE_TEASER_CHARS).trimEnd()}…`
      : note.notes;

  return (
    <Box
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      title={`${stageName}: ${note.notes}`}
      sx={{
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.65,
        py: 0.35,
        mb: 0.35,
        minHeight: 26,
        borderRadius: 1.25,
        border: '1px solid',
        borderColor: NOTES_ACCENT.border,
        borderLeftWidth: 3,
        borderLeftColor: NOTES_ACCENT.main,
        background: NOTES_ACCENT.softBg,
        transition: 'border-color 0.15s ease, background 0.15s ease',
        '&:hover': {
          borderColor: NOTES_ACCENT.borderHover,
          bgcolor: NOTES_ACCENT.softBgStrong,
        },
      }}
    >
      <WarningAmberIcon sx={{ fontSize: 12, color: NOTES_ACCENT.main, flexShrink: 0 }} />
      <Typography
        variant="caption"
        sx={{
          flex: 1,
          minWidth: 0,
          fontWeight: 600,
          color: 'text.primary',
          fontSize: '0.65rem',
          lineHeight: 1.2,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {showStage && (
          <Box component="span" sx={{ color: NOTES_ACCENT.main, fontWeight: 800, mr: 0.4 }}>
            {stageName}
          </Box>
        )}
        {teaser}
      </Typography>
    </Box>
  );
};

const TaxReturnNoteDetailModal: React.FC<{
  open: boolean;
  note: TaxReturnNote | null;
  stageName: string;
  onClose: () => void;
}> = ({ open, note, stageName, onClose }) => {
  if (!note) return null;

  const staff = note.staff;
  const fullName = staffDisplayName(staff);
  const role = staff?.role || staff?.title || staff?.designation || '';
  const whenLabel = formatNoteWhen(note);

  const detailLine = (label: string, value?: string) =>
    value ? (
      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ display: 'block', fontSize: '0.85rem', lineHeight: 1.5, mb: 0.35 }}
      >
        <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {label}:
        </Box>{' '}
        {value}
      </Typography>
    ) : null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          pr: 1,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <WarningAmberIcon sx={{ color: NOTES_ACCENT.main }} />
          <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1.05rem' }} noWrap>
            Team note
          </Typography>
          <Chip
            size="small"
            label={stageName}
            variant="outlined"
            sx={{
              height: 22,
              fontWeight: 700,
              color: NOTES_ACCENT.main,
              borderColor: NOTES_ACCENT.border,
              bgcolor: NOTES_ACCENT.softBg,
            }}
          />
        </Box>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Typography
          variant="body1"
          sx={{
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            lineHeight: 1.55,
            mb: 2,
            color: 'text.primary',
          }}
        >
          {note.notes}
        </Typography>

        <Box
          sx={{
            p: 1.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: NOTES_ACCENT.ring,
            bgcolor: NOTES_ACCENT.softBg,
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
            {fullName}
          </Typography>
          {detailLine('Role', role)}
          {detailLine('Team', staff?.team)}
          {detailLine('Email', staff?.email)}
          {detailLine('Phone', staff?.phone)}
          {whenLabel ? detailLine('Updated', whenLabel) : null}

          {(staff?.phone || staff?.email) && (
            <Box sx={{ display: 'flex', gap: 1, mt: 1.25, flexWrap: 'wrap' }}>
              {staff?.phone ? (
                <Chip
                  component="a"
                  href={`tel:${staff.phone}`}
                  clickable
                  icon={
                    <PhoneIcon
                      sx={{ fontSize: '16px !important', color: `${NOTES_ACCENT.main} !important` }}
                    />
                  }
                  label="Call"
                  size="small"
                  variant="outlined"
                  sx={{
                    color: NOTES_ACCENT.main,
                    borderColor: NOTES_ACCENT.border,
                    bgcolor: '#fff',
                  }}
                />
              ) : null}
              {staff?.email ? (
                <Chip
                  component="a"
                  href={`mailto:${staff.email}`}
                  clickable
                  icon={
                    <EmailIcon
                      sx={{ fontSize: '16px !important', color: `${NOTES_ACCENT.main} !important` }}
                    />
                  }
                  label="Email"
                  size="small"
                  variant="outlined"
                  sx={{
                    color: NOTES_ACCENT.main,
                    borderColor: NOTES_ACCENT.border,
                    bgcolor: '#fff',
                  }}
                />
              ) : null}
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 2.5, py: 1.5 }}>
        <Button onClick={onClose} variant="contained" color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const TrackerReturnPath: React.FC<{
  ticket: TaxReturnTicketTracker;
  notesByStatus: Record<TaxReturnNoteStatus, TaxReturnNote[]>;
}> = ({ ticket, notesByStatus }) => {
  const [activeNote, setActiveNote] = useState<{
    note: TaxReturnNote;
    stageName: string;
  } | null>(null);

  const stages = ticket.stages?.length
    ? ticket.stages
    : [{ name: ticket.currentStageName || 'Status', columnNames: [] }];
  const current = Math.min(
    Math.max(ticket.currentStageIndex ?? 0, 0),
    Math.max(stages.length - 1, 0)
  );

  const currentStage = stages[current];
  // Only notes for the live tracker stage (note.status === ticket.currentStageName)
  const currentStageName = ticket.currentStageName || currentStage?.name || '';
  const currentStageNotes = filterNotesByCurrentStage(
    Object.values(notesByStatus).flat(),
    currentStageName
  );
  const hasCurrentStageNotes = currentStageNotes.length > 0;

  const useLegacyAmber = TRACKER_NOTES_UI === 'legacy';

  const renderStageTeasers = (stageName: string, list: TaxReturnNote[], compactCol: boolean) => {
    const visible = list.slice(0, NOTES_VISIBLE_PER_STAGE);
    const extra = list.length - visible.length;
    return (
      <Box
        sx={{
          flex: compactCol ? 1 : undefined,
          minWidth: 0,
          px: compactCol ? 0.25 : 0,
        }}
      >
        {useLegacyAmber
          ? visible.map((note) => (
              <StatusWarningNote key={note.id} note={note} compact={compactCol} />
            ))
          : visible.map((note) => (
              <StatusNoteTeaser
                key={note.id}
                note={note}
                stageName={stageName}
                showStage={!compactCol}
                onOpen={() => setActiveNote({ note, stageName })}
              />
            ))}
        {extra > 0 && (
          <Button
            size="small"
            onClick={() =>
              setActiveNote({ note: list[NOTES_VISIBLE_PER_STAGE], stageName })
            }
            sx={{
              px: 0.5,
              minWidth: 0,
              fontSize: '0.62rem',
              fontWeight: 700,
              textTransform: 'none',
              color: NOTES_ACCENT.main,
            }}
          >
            +{extra} more
          </Button>
        )}
      </Box>
    );
  };

  return (
    <Box>
      {/* Notes sit above the matching stage column — only current stage notes */}
      {hasCurrentStageNotes && (
        <>
          <Box
            sx={{
              display: { xs: 'flex', sm: 'none' },
              flexDirection: 'column',
              gap: 0.25,
              mb: 1.25,
            }}
          >
            {renderStageTeasers(formatStageLabel(currentStageName), currentStageNotes, false)}
          </Box>

          <Box
            sx={{
              display: { xs: 'none', sm: 'flex' },
              alignItems: 'flex-end',
              gap: 0,
              mb: 1,
            }}
          >
            {stages.map((stage, index) => {
              const isCurrent =
                stage.name.toLowerCase() === currentStageName.toLowerCase();
              const list = isCurrent ? currentStageNotes : [];
              return (
                <React.Fragment key={`note-col-${stage.name}`}>
                  {list.length ? (
                    renderStageTeasers(formatStageLabel(stage.name), list, true)
                  ) : (
                    <Box sx={{ flex: 1, minWidth: 0, px: 0.25 }} />
                  )}
                  {index < stages.length - 1 && (
                    <Box sx={{ width: 10, flexShrink: 0 }} />
                  )}
                </React.Fragment>
              );
            })}
          </Box>
        </>
      )}

      <Box sx={{ mt: 0.5, mb: 1, px: 0, minWidth: 0, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: `repeat(${stages.length}, minmax(0, 1fr))`,
            alignItems: 'start',
            width: '100%',
            minWidth: 0,
          }}
        >
          {stages.map((stage, index) => {
            const done = index < current;
            const on = index === current;
            const hasNotes = on && hasCurrentStageNotes;
            const lineTop = { xs: 13, sm: 15 };

            return (
              <Tooltip
                key={`${ticket.issueKey}-step-${stage.name}`}
                title={
                  on
                    ? hasNotes
                      ? `Current stage: ${formatStageLabel(stage.name)} · ${currentStageNotes.length} note(s)`
                      : `Current stage: ${formatStageLabel(stage.name)}`
                    : done
                      ? `Completed: ${formatStageLabel(stage.name)}`
                      : `Upcoming: ${formatStageLabel(stage.name)}`
                }
                arrow
              >
                <Box
                  sx={{
                    position: 'relative',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    minWidth: 0,
                    width: '100%',
                    overflow: 'hidden',
                  }}
                >
                  {index > 0 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: lineTop,
                        left: 0,
                        width: '50%',
                        height: 3,
                        bgcolor:
                          index <= current
                            ? 'primary.main'
                            : 'rgba(25, 118, 210, 0.18)',
                        zIndex: 0,
                      }}
                    />
                  )}
                  {index < stages.length - 1 && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: lineTop,
                        right: 0,
                        width: '50%',
                        height: 3,
                        bgcolor:
                          index < current
                            ? 'primary.main'
                            : 'rgba(25, 118, 210, 0.18)',
                        zIndex: 0,
                      }}
                    />
                  )}

                  <Box sx={{ position: 'relative', zIndex: 1, flexShrink: 0 }}>
                    {hasNotes && (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: useLegacyAmber ? 'warning.main' : NOTES_ACCENT.main,
                          border: '2px solid #fff',
                          zIndex: 2,
                        }}
                      />
                    )}
                    <Box
                      sx={{
                        width: on ? { xs: 28, sm: 32 } : { xs: 24, sm: 28 },
                        height: on ? { xs: 28, sm: 32 } : { xs: 24, sm: 28 },
                        borderRadius: '50%',
                        display: 'grid',
                        placeItems: 'center',
                        fontSize: on ? 11 : 10,
                        fontWeight: 800,
                        bgcolor: on
                          ? 'success.main'
                          : done
                            ? 'primary.main'
                            : 'background.paper',
                        color: done || on ? 'common.white' : 'text.disabled',
                        border: on ? '3px solid' : '2px solid',
                        borderColor: on
                          ? 'success.dark'
                          : done
                            ? 'primary.main'
                            : 'rgba(25, 118, 210, 0.25)',
                        boxShadow: on ? '0 0 0 3px rgba(76, 175, 80, 0.2)' : 'none',
                      }}
                    >
                      {done && !on ? (
                        <CheckCircleIcon sx={{ fontSize: 14 }} />
                      ) : (
                        index + 1
                      )}
                    </Box>
                  </Box>

                  <Typography
                    variant="caption"
                    sx={{
                      mt: 0.65,
                      width: '100%',
                      maxWidth: '100%',
                      textAlign: 'center',
                      lineHeight: 1.2,
                      fontWeight: on ? 800 : done ? 600 : 500,
                      fontSize: {
                        xs: '0.58rem',
                        sm: on ? '0.72rem' : '0.66rem',
                      },
                      color: on ? 'success.dark' : done ? 'primary.dark' : 'text.secondary',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: { xs: 'normal', sm: 'nowrap' },
                      wordBreak: 'break-word',
                    }}
                    >
                      {formatStageLabel(stage.name)}
                    </Typography>
                    {on && (
                      <Chip
                        size="small"
                        color="success"
                        label="Current"
                      sx={{
                        mt: 0.35,
                        height: 16,
                        maxWidth: '100%',
                        fontSize: '0.55rem',
                        fontWeight: 800,
                        '& .MuiChip-label': { px: 0.5 },
                      }}
                    />
                  )}
                </Box>
              </Tooltip>
            );
          })}
        </Box>
      </Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', mt: 0.25, px: 0 }}
      >
        {[
          ticket.issueKey,
          formatTaxYear(ticket.taxYear)
            ? `Tax year ${formatTaxYear(ticket.taxYear)}`
            : '',
          ticket.currentColumnName || ticket.statusName,
        ]
          .filter(Boolean)
          .join(' · ')}
      </Typography>

      <TaxReturnNoteDetailModal
        open={!!activeNote}
        note={activeNote?.note || null}
        stageName={activeNote?.stageName || ''}
        onClose={() => setActiveNote(null)}
      />
    </Box>
  );
};

const STAGE_PLACEHOLDER_COUNT = STAGE_COLUMN_MAP.length || 5;

/** Shimmer placeholder — no "Status tracker" header until real data exists */
const TaxReturnStatusTrackerShimmer: React.FC = () => (
  <Box
    aria-busy="true"
    aria-label="Loading return status"
    sx={{
      ...tileSx,
      px: 0.75,
      py: 1.25,
      mb: 4,
      minWidth: 0,
      maxWidth: '100%',
      overflow: 'hidden',
      background: 'linear-gradient(160deg, #ffffff 0%, #f5f9fc 100%)',
      pointerEvents: 'none',
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
      <Skeleton variant="rounded" width={36} height={36} sx={{ borderRadius: 2, flexShrink: 0 }} />
      <Box sx={{ flex: 1, minWidth: 0, pt: 0.25 }}>
        <Skeleton variant="text" width={120} height={22} sx={{ mb: 0.5 }} />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Skeleton variant="text" width={110} height={20} />
          <Skeleton variant="rounded" width={88} height={28} sx={{ borderRadius: 2 }} />
        </Box>
      </Box>
    </Box>

    <Box
      sx={{
        mt: 2,
        display: 'grid',
        gridTemplateColumns: `repeat(${STAGE_PLACEHOLDER_COUNT}, minmax(0, 1fr))`,
        alignItems: 'start',
        width: '100%',
        gap: 0,
      }}
    >
      {Array.from({ length: STAGE_PLACEHOLDER_COUNT }).map((_, index) => (
        <Box
          key={`shimmer-stage-${index}`}
          sx={{
            position: 'relative',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            minWidth: 0,
            width: '100%',
          }}
        >
          {index > 0 && (
            <Skeleton
              variant="rectangular"
              sx={{
                position: 'absolute',
                top: 14,
                left: 0,
                width: '50%',
                height: 3,
                borderRadius: 1,
              }}
            />
          )}
          {index < STAGE_PLACEHOLDER_COUNT - 1 && (
            <Skeleton
              variant="rectangular"
              sx={{
                position: 'absolute',
                top: 14,
                right: 0,
                width: '50%',
                height: 3,
                borderRadius: 1,
              }}
            />
          )}
          <Skeleton variant="circular" width={28} height={28} sx={{ position: 'relative', zIndex: 1 }} />
          <Skeleton variant="text" width="70%" height={14} sx={{ mt: 0.75 }} />
        </Box>
      ))}
    </Box>

    <Skeleton variant="text" width={160} height={14} sx={{ mt: 1.25 }} />
  </Box>
);

const TaxReturnStatusTracker: React.FC<TaxReturnStatusTrackerProps> = ({
  loading,
  error,
  result,
  notes,
}) => {
  const tickets = result?.tickets || [];
  const primaryTicket = tickets[0];
  // PB returns all company notes; UI only surfaces the current tracker stage.
  const visibleNotes = filterNotesByCurrentStage(
    notes || [],
    primaryTicket?.currentStageName
  );
  const notesByStatus = groupNotesByStatus(visibleNotes);
  const hasTaxReturnNotes = visibleNotes.length > 0;

  // Loading from Jira — shimmer only (no real Status tracker header)
  if (loading) {
    return <TaxReturnStatusTrackerShimmer />;
  }

  // No status for this company — hide the entire status card widget
  if (!primaryTicket) {
    return null;
  }

  return (
    <Box
      sx={{
        ...tileSx,
        px: 0.75,
        py: 1.25,
        mb: 4,
        display: 'flex',
        flexDirection: 'column',
        minWidth: 0,
        maxWidth: '100%',
        overflow: 'hidden',
        background: 'linear-gradient(160deg, #ffffff 0%, #f5f9fc 100%)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1,
          flexShrink: 0,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25, minWidth: 0 }}>
          <IconWell>
            <TimelineIcon sx={{ fontSize: 20 }} />
          </IconWell>
          <Box sx={{ minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 700, color: 'text.primary' }}>
                Status tracker
              </Typography>
              {!!formatTaxYear(primaryTicket.taxYear) && (
                <Chip
                  size="small"
                  label={`Tax year ${formatTaxYear(primaryTicket.taxYear)}`}
                  variant="outlined"
                  sx={{
                    height: 22,
                    fontWeight: 700,
                    fontSize: '0.7rem',
                    borderColor: 'rgba(25, 118, 210, 0.35)',
                    color: 'primary.dark',
                    bgcolor: 'rgba(25, 118, 210, 0.06)',
                  }}
                />
              )}
            </Box>
            {!error && (
              <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                  You are currently on
                </Typography>
                <Chip
                  color="success"
                  label={formatStageLabel(primaryTicket.currentStageName)}
                  sx={{
                    fontWeight: 800,
                    fontSize: '0.85rem',
                    height: 28,
                    boxShadow: '0 2px 8px rgba(76, 175, 80, 0.3)',
                  }}
                />
                {tickets.length > 1 && (
                  <Typography variant="body2" color="text.secondary">
                    · {tickets.length} tickets
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          mt: 0.5,
          overflowY: hasTaxReturnNotes ? 'auto' : 'hidden',
          pr: hasTaxReturnNotes ? 0.5 : 0,
        }}
      >
        {error && (
          <Alert severity="warning" sx={{ mt: 1, mb: 1, borderRadius: 2, py: 0 }}>
            {error}
          </Alert>
        )}
        <TrackerReturnPath ticket={primaryTicket} notesByStatus={notesByStatus} />
        {tickets.length > 1 && (
          <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
            {tickets.slice(1).map((t) => (
              <Chip
                key={t.issueKey}
                size="small"
                color="primary"
                variant="outlined"
                label={`${t.issueKey}: ${formatStageLabel(t.currentStageName)}`}
                sx={{ fontWeight: 600, height: 24 }}
              />
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default TaxReturnStatusTracker;
